import { RouterLink, RouterLinkActive } from '@angular/router';
import { NgClass, NgStyle, NgTemplateOutlet } from '@angular/common';
import { AfterContentInit, ChangeDetectionStrategy, Component, ContentChild, ContentChildren, HostBinding, input, Input, InputSignal, QueryList, ViewChild } from '@angular/core';

/**
 * libs
 */
import { Dropdown } from 'bootstrap';

/**
 * interfaces
*/
import { IDropdownItem } from './interfaces/IDropdownItem';

/**
 * enums
*/
import { DropdownDirection, IDropdownDirection } from './enums/dropdown-directions';

/**
 * directives
*/
import { ButtonDirective } from '@sharedDirectives/button/button.directive';
import { ITemplateRef } from '@sharedDirectives/templates/interfaces/template';
import { TemplateDirective } from '@sharedDirectives/templates/template.directive';
import { WavesEffectDirective } from '@sharedDirectives/node-waves/waves-effect.directive';
import { DropdownButtonDirective } from '@sharedDirectives/dropdown-button/dropdown-button.directive';


@Component({
  selector: 'app-dropdown',
  templateUrl: './dropdown.component.html',
  styleUrl: './dropdown.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgClass,
    NgStyle,
    RouterLink,
    RouterLinkActive,
    NgTemplateOutlet,
    ButtonDirective,
    WavesEffectDirective,
    DropdownButtonDirective
  ],
})
export class DropdownComponent implements AfterContentInit{

  /**
   * @description set `directions` menu ( down, up, start, end, etc... )
   */
  @HostBinding('class')
  @Input({ alias: 'directions' }) 
  public directions: IDropdownDirection = DropdownDirection.Dropdown

  /**
   * @description Wrap with .btn-group (used for split buttons)
   */
  @HostBinding('class.btn-group')
  @Input({ alias: 'groupBtn' }) public isGroup: boolean = false;
  
  
  /**
   *  @description bootstrap Variants ( Primary, etc.. )
   */
  @Input({ alias: 'buttonVariant' }) 
  public buttonVariant!: ButtonDirective['variant']

  /**
   * @description bootstrap Appearance ( Label, Outline, Text )
   */
  @Input({ alias: 'buttonAppearance' })
  public buttonAppearance!: ButtonDirective['appearance']

  /**
   * @description bootstrap button sizes ( xs, sm, md, lg, xl )
   */
  @Input({ alias: 'buttonSize' })
  public buttonSize!: ButtonDirective['size']

  /**
   * @description button context
   */
  @Input({ alias: 'dropdownButtonText' })
  public dropdownButtonText!: string

  /**
   * @description button icon
   */
  @Input({ alias: 'icon' })
  public dropdownButtonIcon!: string
  
  /**
   * @description dropdown button without context
   */
  @Input({ alias: "iconOnly"})
  public IconOnly: boolean = false;

  /**
   * @description hide dropdown arrow
   */
  @Input({ alias: 'hideArrow' })
  public hideArrow: boolean = false;

  /**
   * @description Disables the dropdown trigger button when `true`.
   */
  @Input({ alias: 'dropdownButtonDisabled' })
  public dropdownButtonDisabled: boolean = false;

  /** */
  public readonly reference: InputSignal<Dropdown.Options['reference']> = input<Dropdown.Options['reference']>('toggle')

  /**
   * @description CSS classes for button container
   */
  @Input({ 
    alias: 'buttonClassStyle',
    transform: (value: string | string[]) => {
      return Array.isArray(value) ? value.join(' ') : value
    }
  })
  public dropdownButtonClassStyle!: string | string[]

  /**
   * @description Menu CSS classes
   */
  @Input({ alias: 'dropdownButtonSplit' })
  public set isSplit(value: boolean) {
    this._isSplit = value
    if (this._isSplit) this.isGroup = this._isSplit
  }
  public get isSplit(): boolean {
    return this._isSplit
  }
  private _isSplit: boolean = false;

  /**
   * @description Menu CSS classes
   */
  @Input({ alias: 'menuClassStyle' })
  public dropdownMenuClassStyle!: string | string[] 

  /**
   * @description List of items to be rendered in dropdown menu
   */
  @Input({ alias: 'items' })
  public items: IDropdownItem[] = []  

  /**
   * @description Query for all custom templates projected via TemplateDirective.
   */
  @ContentChildren(TemplateDirective)
  public TemplatesDirectiveRef!: QueryList<TemplateDirective>;

  /**
   * 
   */
  @ViewChild(DropdownButtonDirective)
  private set _dropdownButtonViewRef(btn: DropdownButtonDirective) {
    if (btn != null) this.dropdownButtonDirectiveRef = btn; 
  }

  /**
   * 
   */
  @ContentChild(DropdownButtonDirective)
  private set _dropdownButtonContainerRef(btn: DropdownButtonDirective) {
    if (btn != null) this.dropdownButtonDirectiveRef = btn;
  }  

  /**
   * 
   */
  @ViewChild(DropdownButtonDirective, { read: ButtonDirective })
  private set _buttonDirectiveViewRef(btn: ButtonDirective ) {
    if (btn != null) this.buttonDirectiveRef = btn;
  }

  /**
   * 
   */
  @ContentChild(DropdownButtonDirective, { read: ButtonDirective })
  private set _buttonDirectiveContentRef(btn: ButtonDirective ) {
    if (btn != null) this.buttonDirectiveRef = btn;
  }

  /**
   * @description to access `ButtonDirective` whatever as viewInit or content projection
   */
  public buttonDirectiveRef!: ButtonDirective

  /**
   * @description to access `DropdownButtonDirective` whatever as viewInit or content projection
   */
  public dropdownButtonDirectiveRef!: DropdownButtonDirective

  /**
   * @description Holds the resolved template reference for dropdownMenu section.
   */
  public dropdownMenuTemplate!: ITemplateRef<any> | null;

  /**
   * @description Holds the resolved template reference for dropdownButton section.
   */
  public dropdownButtonTemplate!: ITemplateRef<any> | null;

  /**
   * @description Holds the resolved template reference for dropdownButton section.
   */
  public splitDefaultButtonTemplate!: ITemplateRef<any> | null;


  /**
   *
   */
  constructor() {}


  ngAfterContentInit(): void {

    // console.log('------------ ngAfterContentInit ------------')
    if (this.TemplatesDirectiveRef && this.TemplatesDirectiveRef.length > 0) {
      this.dropdownMenuTemplate = TemplateDirective.findDirective<any>(this.TemplatesDirectiveRef, 'dropdownMenuTemplate');
      this.dropdownButtonTemplate = TemplateDirective.findDirective<any>(this.TemplatesDirectiveRef, 'dropdownButtonTemplate');
      this.splitDefaultButtonTemplate = TemplateDirective.findDirective<any>(this.TemplatesDirectiveRef, 'splitDefaultButtonTemplate');
    }
    
  }

  /**
   * @description Executes the command function associated with the dropdown item.
   * @param item - The dropdown item containing the command function.
   * @returns void
   */
  public commandHandler(item: IDropdownItem): void {

    // check if command is a function
    if (!(item?.command instanceof Function)) return void(0);

    // execute command
    item.command()
  }

  /**
   * Finds a projected template by name.
   * @param tempName - Template name (e.g. 'dropdownMenuTemplate', 'dropdownButtonTemplate').
   * @returns The matching {@link TemplateDirective} or undefined.
   */
  public getTemplate(tempName: string): TemplateDirective | undefined {
    return this.TemplatesDirectiveRef.find(temp => temp.name == tempName)
  }

}
